// src/pages/JuzPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Heading, Text, SimpleGrid, Card, CardBody, Button, Spinner, Alert, AlertIcon,
  Select, useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight, List } from 'lucide-react';
import QuranShell from '../components/Quran/QuranShell';
import ReaderSettings from '../components/Quran/ReaderSettings';
import AyahCard, { BISMILLAH } from '../components/Quran/AyahCard';
import useQuranSettings from '../hooks/useQuranSettings';
import useRecitations from '../hooks/useRecitations';
import useAyahPlayer from '../hooks/useAyahPlayer';
import { fetchJuz } from '../api/quranService';
import { juzList } from '../data/juzMeta';
import { getSurah } from '../data/surahMeta';


// ============ JUZ INDEX ============

const JuzIndex = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  return (
    <>
      <Box mb={6}>
        <Heading size="lg" mb={1}>Juz</Heading>
        <Text color="gray.500">The Quran in its 30 parts, a traditional way to divide daily reading.</Text>
      </Box>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={4}>
        {juzList.map((juz) => {
          const surah = getSurah(juz.surah);
          return (
            <Card
              key={juz.id} as={RouterLink} to={`/juz/${juz.id}`}
              bg={cardBg} variant="outline" borderColor={borderColor} borderRadius="xl"
              transition="all 0.15s" _hover={{ borderColor: 'teal.400', boxShadow: 'md' }}
            >
              <CardBody>
                <Text fontSize="xs" color="teal.600" fontWeight="bold" textTransform="uppercase">Juz {juz.id}</Text>
                <Text fontWeight="semibold" mt={1} noOfLines={1}>{surah?.name}</Text>
                <Text fontSize="xs" color={subtextColor}>Starts at {juz.surah}:{juz.ayah}</Text>
              </CardBody>
            </Card>
          );
        })}
      </SimpleGrid>
    </>
  );
};


// ============ JUZ READER ============

const SurahHeading = ({ surahId, settings }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const surah = getSurah(surahId);
  return (
    <Box textAlign="center" bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" py={4} mt={8} mb={2}>
      <Text fontFamily="'Amiri Quran', serif" fontSize="2xl" lineHeight="1.6">{surah?.arabicName}</Text>
      <Text fontWeight="semibold">{surahId}. {surah?.name}</Text>
      {surahId !== 1 && surahId !== 9 && (
        <Text fontFamily={settings.fontFamily} fontSize="2xl" dir="rtl" mt={2}>{BISMILLAH}</Text>
      )}
    </Box>
  );
};

const JuzReader = ({ juzId, player }) => {
  const navigate = useNavigate();
  const settings = useQuranSettings();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { stop } = player;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    fetchJuz(juzId, settings.translationId)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setError('This juz could not be loaded.'));
    return () => { cancelled = true; };
  }, [juzId, settings.translationId]);

  useEffect(() => stop, [juzId, settings.reciterId, stop]);

  const recordings = useRecitations(data?.surah_ids, settings.reciterId);
  const ayahs = useMemo(() => data?.ayahs || [], [data]);

  const queue = useMemo(() => ayahs
    .filter((a) => recordings.ayahs[a.ayah_key])
    .map((a) => ({
      id: a.ayah_key,
      label: `${getSurah(a.surah_id)?.name} · Ayah ${a.ayah_index}`,
      sublabel: `Juz ${juzId} · ${settings.reciterName}`,
      mediaKey: recordings.ayahs[a.ayah_key].mediaKey,
    })), [ayahs, recordings, juzId, settings.reciterName]);

  const playingAll = queue.some((q) => player.isPlaying(q.id));
  const fileName = (ayah) => `${ayah.ayah_key.replace(':', '_')}_${settings.reciterName}.mp3`.replace(/\s+/g, '_');

  return (
    <>
      <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
        <Button as={RouterLink} to="/juz" size="sm" variant="ghost" leftIcon={<List size={16} />}>All juz</Button>
        <Select size="sm" maxW="160px" value={juzId} aria-label="Go to juz" onChange={(e) => navigate(`/juz/${e.target.value}`)}>
          {juzList.map((j) => <option key={j.id} value={j.id}>Juz {j.id}</option>)}
        </Select>
      </Flex>

      <Flex justify="space-between" align="center" mb={6} gap={4} wrap="wrap">
        <Box>
          <Heading size="lg">Juz {juzId}</Heading>
          {ayahs.length > 0 && (
            <Text color="gray.500">
              {ayahs[0].ayah_key} to {ayahs[ayahs.length - 1].ayah_key} · {ayahs.length} ayahs
            </Text>
          )}
        </Box>
        <Button
          colorScheme="teal" leftIcon={playingAll ? <Pause size={16} /> : <Play size={16} />}
          onClick={() => (playingAll ? player.toggle() : player.playFrom(queue, 0))}
          isDisabled={queue.length === 0} isLoading={!!data && recordings.loading} loadingText="Loading audio"
        >
          {playingAll ? 'Pause' : 'Play juz'}
        </Button>
      </Flex>

      <ReaderSettings settings={settings} />

      {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
      {!data && !error && <Flex justify="center" py={16}><Spinner size="xl" color="teal.500" /></Flex>}

      {ayahs.map((ayah) => {
        const surahId = Number(ayah.surah_id);
        return (
          <React.Fragment key={ayah.ayah_key}>
            {Number(ayah.ayah_index) === 1 && <SurahHeading surahId={surahId} settings={settings} />}
            <AyahCard
              ayah={ayah}
              settings={settings}
              recording={recordings.ayahs[ayah.ayah_key]}
              isActive={player.isActive(ayah.ayah_key)}
              isPlaying={player.isPlaying(ayah.ayah_key)}
              onPlay={() => player.playFrom(queue, queue.findIndex((q) => q.id === ayah.ayah_key))}
              downloadName={fileName(ayah)}
            />
          </React.Fragment>
        );
      })}

      <Flex justify="space-between" mt={8}>
        {juzId > 1 ? (
          <Button as={RouterLink} to={`/juz/${juzId - 1}`} variant="outline" leftIcon={<ChevronLeft size={16} />}>Juz {juzId - 1}</Button>
        ) : <span />}
        {juzId < 30 && (
          <Button as={RouterLink} to={`/juz/${juzId + 1}`} variant="outline" rightIcon={<ChevronRight size={16} />}>Juz {juzId + 1}</Button>
        )}
      </Flex>
    </>
  );
};


// ============ PAGE ============

const JuzPage = () => {
  const { juzId } = useParams();
  const id = Number(juzId);
  const valid = Number.isInteger(id) && id >= 1 && id <= 30;
  const player = useAyahPlayer();

  return (
    <QuranShell player={valid ? player : undefined}>
      {valid ? <JuzReader juzId={id} player={player} /> : <JuzIndex />}
    </QuranShell>
  );
};

export default JuzPage;
